import React, { useEffect, useState } from "react";
import { Chart } from "react-google-charts";
import { Tabs, Tab, TabList, TabPanel } from "react-tabs";
import DiagramService from "../../../../../services/diagrams/DiagramService";
import {
  VolumeMaxVolumeAvgVolumeForEachExerciseEachTraining,
  TimeMaxTimeAvgTimeForEachExerciseEachTraining,
} from "../../../../../types/types";
import { withTranslation } from "react-i18next";

interface Props {
  workbookId: number;
  t: any;
}

const DiagramPerExerciseUnified: React.FC<Props> = ({ workbookId, t }) => {
  const [volumeData, setVolumeData] = useState<VolumeMaxVolumeAvgVolumeForEachExerciseEachTraining[]>([]);
  const [timeData, setTimeData] = useState<TimeMaxTimeAvgTimeForEachExerciseEachTraining[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      DiagramService.getVolumeMaxVolumeAvgVolumeForEachExerciseEachTraining(workbookId),
      DiagramService.getTimeMaxTimeAvgTimeForEachExerciseEachTraining(workbookId)
    ])
      .then(([volumeRes, timeRes]) => {
        setVolumeData(volumeRes.data);
        setTimeData(timeRes.data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching unified chart data", err);
        setLoading(false);
      });
  }, [workbookId]);

  if (loading) return <div style={{ textAlign: "center", marginTop: "20px" }}>
                        <span className="loader" />
                      </div>;

  const categories = Array.from(new Set([
    ...volumeData.map((d) => d.category),
    ...timeData.map((d) => d.category)
  ]));

  const renderChart = (
    exercise: string,
    exerciseData: any[],
    isTime: boolean
  ) => {
    const chartData = [
      ["Date"
        , t("diagram.series") + " 1"
        , t("diagram.series") + " 2"
        , t("diagram.series") + " 3"
        , t("diagram.series") + " 4"
        , t("diagram.series") + " 5"
        , t("diagram.avarage")
        , t("diagram.max")],
      ...exerciseData.map((d) => [
        d.date,
        isTime ? d.timeCol01 : d.volumeCol01,
        isTime ? d.timeCol02 : d.volumeCol02,
        isTime ? d.timeCol03 : d.volumeCol03,
        isTime ? d.timeCol04 : d.volumeCol04,
        isTime ? d.timeCol05 : d.volumeCol05,
        isTime ? d.timeAvg : d.volumeAvg,
        isTime ? d.timeMax : d.volumeMax,
      ])
    ];

    const options = {
      animation: {
        startup: false,
        duration: 2000,
      },
      title: `${exercise}${isTime ? " (" + t("diagram.time") + ")" : " (" + t("diagram.weight") + ")"}`,
      titleTextStyle: { color: '#8e7f90' },
      backgroundColor: 'transparent',
      hAxis: {
        title: "Date",
        titleTextStyle: { color: '#8e7f90' },
        textStyle: { color: '#8e7f90' },
      },
      vAxis: {
        title: isTime ? "[s]" : "[kg]",
        titleTextStyle: { color: '#8e7f90' },
        textStyle: { color: '#8e7f90' },
        minValue: 0,
      },
      seriesType: "bars",
      series: {
        0: { color: isTime ? "#f57c00" : "#4caf50" },
        1: { color: isTime ? "#f57c00" : "#4caf50" },
        2: { color: isTime ? "#f57c00" : "#4caf50" },
        3: { color: isTime ? "#f57c00" : "#4caf50" },
        4: { color: isTime ? "#f57c00" : "#4caf50" },
        5: { type: "line", color: "#3366CC" }, // avg
        6: { type: "line", color: "#800080" }, // max
      },
      legend: { textStyle: { color: '#8e7f90' } },
    };

    return (
      <div key={exercise} style={{ marginBottom: "2rem", overflowX: "auto", overflowY: "hidden" }}>
        <div style={{ minWidth: `${exerciseData.length * 40}px` }}>
          <Chart
            chartType="ComboChart"
            width="100%"
            height="400px"
            data={chartData}
            options={options}
          />
        </div>
      </div>
    );
  };

  return (
    <Tabs>
      <TabList>
        {categories.map((category) => (
          <Tab key={category}>{category}</Tab>
        ))}
      </TabList>

      {categories.map((category) => {
        const exercises = Array.from(new Set([
          ...volumeData.filter(d => d.category === category).map(d => d.exercise),
          ...timeData.filter(d => d.category === category).map(d => d.exercise)
        ]));

        return (
          <TabPanel key={category}>
            {exercises.map((exercise) => {
              const volumeExerciseData = volumeData
                .filter((d) => d.category === category && d.exercise === exercise)
                .sort((a, b) => a.date.localeCompare(b.date));

              const timeExerciseData = timeData
                .filter((d) => d.category === category && d.exercise === exercise)
                .sort((a, b) => a.date.localeCompare(b.date));

              return (
                <div key={exercise}>
                  {volumeExerciseData.length > 0 && renderChart(exercise, volumeExerciseData, false)}
                  {timeExerciseData.length > 0 && renderChart(exercise, timeExerciseData, true)}
                </div>
              );
            })}
          </TabPanel>
        );
      })}
    </Tabs>
  );
};

export default withTranslation("global")(DiagramPerExerciseUnified);
